import { getPlayerAvatar } from '../../utils/player';
import { comparePlayers } from '../../utils/matchStats';
import { formatWinPct } from '../../utils/formatStats';

// Standings table for the Events and Overall tabs. Rows are match stats from the shared
// matchStats module (#34) and are sorted the same way: W -> Win % -> game difference (MVPs never affect order).
// showMvp hides the MVP column on classic-only tables (classic matches never award MVPs).
// view narrows the columns (#54): 'all' | 'matches' (W, L, Win %) | 'games' (GW, GL, +/-) | 'mvp'.
// Row order never changes with the view.
export const STATS_VIEWS = [
  { value: 'matches', label: 'Matches' },
  { value: 'games', label: 'Games' },
  { value: 'mvp', label: 'MVP' },
];

const formatGameDiff = (data) => {
  const diff = (data.gw ?? 0) - (data.gl ?? 0);
  return diff > 0 ? `+${diff}` : String(diff);
};

const PlayerStats = ({ stats, playersInSession, allPlayers, showMvp = false, currentUser = null, view = 'all' }) => {
  const showMatches = view === 'all' || view === 'matches';
  const showGames = view === 'all' || view === 'games';
  const showMvpCol = showMvp && (view === 'all' || view === 'mvp');
  const gamesClass = view === 'all' ? 'col-secondary' : undefined;
  const filteredStats = Object.fromEntries(
    Object.entries(stats || {}).filter(([player]) => {
      if (!player || player.toLowerCase().trim() === 'tie') return false;
      if (playersInSession) {
        return playersInSession.some((p) => p && p.name === player);
      }
      return true;
    })
  );

  const isRowCurrentUser = (playerName) => {
    if (!currentUser) return false;
    if (currentUser.displayName && playerName.toLowerCase().trim() === currentUser.displayName.toLowerCase().trim()) {
      return true;
    }
    const playerObj = (allPlayers || []).find(p => p.name === playerName);
    if (playerObj && currentUser.uid && playerObj.uid === currentUser.uid) {
      return true;
    }
    return false;
  };

  const sortedPlayers = Object.entries(filteredStats)
    .filter(([, data]) => {
      const matches = (data.w ?? data.wins ?? 0) + (data.l ?? data.losses ?? 0);
      const games = (data.gw ?? 0) + (data.gl ?? 0);
      return matches > 0 || games > 0;
    })
    .sort(([nameA, dataA], [nameB, dataB]) =>
      comparePlayers({ name: nameA, ...dataA }, { name: nameB, ...dataB })
    );

  if (sortedPlayers.length === 0) {
    return (
      <div className="card-content text-center" style={{ padding: 'var(--spacing-xl)' }}>
        <p className="body-large" style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--primary)' }}>
          No data available yet
        </p>
        <p className="body-small">
          Complete a match to see player data.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="table-container">
        <table className="table table-stats">
          <thead>
            <tr>
              <th>Player</th>
              {showMatches && <th><abbr title="Match wins">W</abbr></th>}
              {showMatches && <th><abbr title="Match losses">L</abbr></th>}
              {showGames && <th className={gamesClass}><abbr title="Games won">GW</abbr></th>}
              {showGames && <th className={gamesClass}><abbr title="Games lost">GL</abbr></th>}
              {view === 'games' && <th><abbr title="Game difference">+/-</abbr></th>}
              {showMatches && <th><abbr title="Match win percentage">Win %</abbr></th>}
              {showMvpCol && <th><abbr title="Rotation session MVPs">MVP</abbr></th>}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map(([player, data]) => {
              const isHighlight = isRowCurrentUser(player);

              return (
                <tr
                  key={player}
                  className={isHighlight ? 'bg-[#1b2e5a] font-semibold text-white' : ''}
                  style={isHighlight ? { backgroundColor: 'var(--on-primary, #1b2e5a)', color: '#ffffff' } : undefined}
                >
                  <td>
                    <div className="flex items-center gap-sm">
                      <img
                        src={getPlayerAvatar(allPlayers, player)}
                        alt={`${player}'s avatar`}
                        className="avatar avatar-sm"
                      />
                      <span className={`table-stats-name ${isHighlight ? 'font-bold text-white' : ''}`}>{player}</span>
                    </div>
                  </td>
                  {showMatches && <td>{data.w ?? data.wins ?? 0}</td>}
                  {showMatches && <td>{data.l ?? data.losses ?? 0}</td>}
                  {showGames && <td className={gamesClass}>{data.gw ?? 0}</td>}
                  {showGames && <td className={gamesClass}>{data.gl ?? 0}</td>}
                  {view === 'games' && <td>{formatGameDiff(data)}</td>}
                  {showMatches && <td>{formatWinPct(data)}</td>}
                  {showMvpCol && <td>{data.mvp ?? data.mvpCount ?? 0}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="table-stats-legend">
        {[
          showMatches && <><strong>W / L</strong> matches won and lost</>,
          showGames && <><strong>GW / GL</strong> games won and lost</>,
          view === 'games' && <><strong>+/-</strong> game difference</>,
          showMvpCol && <><strong>MVP</strong> rotation match winner</>,
        ]
          .filter(Boolean)
          .map((item, i) => <span key={i}>{i > 0 && ' · '}{item}</span>)}
        <br />
        Ranked by W, then Win %, then game difference.
      </p>
    </>
  );
};

export default PlayerStats;
